import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useCart } from '../context/CartContext';

const STATIC_MESSAGES = [
    { line1: "Masz pytanie?", line2: "Nasi eksperci czekają" },
    { line1: "Czat 24/7", line2: "Zadaj pytanie teraz" },
    { line1: "Potrzebujesz porady?", line2: "Możemy porozmawiać" },
    { line1: "Przyjmij energię", line2: "którą Ci wysyłamy" },
];

const FloatingChatButton: React.FC = () => {
    const location = useLocation();
    const { unreadCount, conversations } = useCart();

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const timeoutRef = useRef<number | null>(null);
    const [messageWidths, setMessageWidths] = useState<number[]>([]);
    const widthRefs = useRef<(HTMLDivElement | null)[]>([]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const prevUnreadCountRef = useRef(unreadCount);

    // Determine which messages and avatars to show
    const unreadConversations = useMemo(() =>
        conversations.filter(c => c.unread_count && c.unread_count > 0).slice(0, 3),
        [conversations]
    );

    const dynamicMessages = useMemo(() =>
        unreadCount > 0
            ? unreadConversations.map(c => ({ line1: c.name, line2: 'Nowa wiadomość' }))
            : STATIC_MESSAGES,
        [unreadCount, unreadConversations]
    );
    
    // Create a stable key representing the messages content to prevent animation resets on background data refresh
    const dynamicMessagesKey = JSON.stringify(dynamicMessages);

    const unreadAvatars = useMemo(() =>
        unreadConversations.map(c => c.avatar_img).filter((avatar): avatar is string => !!avatar),
        [unreadConversations]
    );

    // Play sound on new message
    useEffect(() => {
        if (unreadCount > prevUnreadCountRef.current) {
            audioRef.current?.play().catch(error => console.log("Audio play failed:", error));
        }
        prevUnreadCountRef.current = unreadCount;
    }, [unreadCount]);

    // Reset animation and recalculate widths ONLY when message content actually changes
    useEffect(() => {
        setCurrentMessageIndex(0);
        setIsCollapsed(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        
        // Defer width measurement to allow refs to update after re-render
        const measureTimeout = setTimeout(() => {
             const widths = widthRefs.current.map(el => el ? el.scrollWidth : 0);
             setMessageWidths(widths);
        }, 100);

        return () => clearTimeout(measureTimeout);
    }, [dynamicMessagesKey]);

    // Unified animation cycle controller
    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (dynamicMessages.length === 0) return;

        const timer = isCollapsed
            // If collapsed, wait 10s then expand and reset to the first message.
            ? setTimeout(() => {
                setIsCollapsed(false);
                setCurrentMessageIndex(0);
              }, 30000)
            // If not collapsed, wait 4s then advance to the next message or collapse.
            : setTimeout(() => {
                setCurrentMessageIndex(prevIndex => {
                    const nextIndex = prevIndex + 1;
                    // If we've shown the last message, trigger collapse.
                    if (nextIndex >= dynamicMessages.length) {
                        setIsCollapsed(true);
                        return prevIndex; // Stay on the last message for the collapse animation
                    }
                    // Otherwise, just move to the next message.
                    return nextIndex;
                });
              }, 4000);

        // Cleanup function to clear the timer on re-render or unmount.
        return () => clearTimeout(timer);
    }, [isCollapsed, currentMessageIndex, dynamicMessages]); // Depends on the actual array to keep running

    // Hide component on chat, portal and CMS pages
    if (
        location.pathname === '/czat' ||
        location.pathname === '/portal' ||
        location.pathname.startsWith('/cms')
    ) {
        return null;
    }

    const messageContainerHeight = '2.5rem';
    const currentWidth = messageWidths[currentMessageIndex] ?? 0;
    const showAvatars = unreadCount > 0 && unreadAvatars.length > 0;

    return (
        <>
            <audio ref={audioRef} src="https://assets.esoteric.pl/audio/new-message.mp3" preload="auto" />
            
            <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', visibility: 'hidden', pointerEvents: 'none' }}>
                {dynamicMessages.map((msg, index) => (
                    <div ref={el => { widthRefs.current[index] = el; }} key={index} className="inline-block">
                        <div className="flex flex-col text-right pl-4">
                            <span className="text-sm font-bold leading-tight whitespace-nowrap">{msg.line1}</span>
                            <span className="text-xs text-gray-400 leading-tight whitespace-nowrap">{msg.line2}</span>
                        </div>
                    </div>
                ))}
            </div>

            <a 
                href="/czat"
                className={`fixed bottom-4 right-4 z-50 flex items-center bg-mystic-box rounded-full shadow-lg text-white no-underline group hover:bg-mystic-btn transition-all duration-300 animate-fade-in`}
                aria-label={`Przejdź do czatu, ${unreadCount} nieprzeczytanych wiadomości`}
            >
                {dynamicMessages.length > 0 && (
                    <div 
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isCollapsed ? '' : 'mr-3'}`}
                        style={{ height: messageContainerHeight, width: isCollapsed ? 0 : `${currentWidth}px` }}
                    >
                        <div 
                            className="flex flex-col transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateY(calc(-${currentMessageIndex} * ${messageContainerHeight}))` }}
                        >
                            {dynamicMessages.map((msg, index) => (
                                <div 
                                    key={index} 
                                    className="flex flex-col text-right pl-4"
                                    style={{ height: messageContainerHeight, justifyContent: 'center' }}
                                >
                                    <span className="text-sm font-bold leading-tight whitespace-nowrap">{msg.line1}</span>
                                    <span className="text-xs text-gray-400 leading-tight whitespace-nowrap">{msg.line2}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center shrink-0">
                    {showAvatars && unreadAvatars.map((avatarUrl, index) => (
                        <img
                            key={index}
                            src={avatarUrl}
                            alt={`Wiadomość od eksperta ${index + 1}`}
                            className="w-12 h-12 rounded-full object-cover -mr-6"
                        />
                    ))}
                    
                    <div className="relative w-12 h-12 rounded-full bg-mystic-accent flex items-center justify-center text-black shadow-md shrink-0 z-10">
                        <MessageSquare className="w-6 h-6" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center border-2 border-mystic-box font-bold z-20">
                                {unreadCount}
                            </span>
                        )}
                    </div>
                </div>
            </a>
        </>
    );
};

export default FloatingChatButton;